import type { Order, PaymentMethod } from '@commercelayer/sdk'

/**
 * Type guard to check if a payment method is an Adyen payment method.
 *
 * @param {PaymentMethod} p - The payment method to check.
 * @returns {boolean} True if the payment method is an Adyen payment method, false otherwise.
 * @typeparam {PaymentMethod & { payment_source_type: 'adyen_payments' }} - Refined type for Adyen payment methods.
 * @example
 * const paymentMethod = { payment_source_type: 'adyen_payments' };
 * if (isAdyenPaymentMethod(paymentMethod)) {
 *   // paymentMethod is confirmed to be an Adyen payment method
 * }
 */
export const isAdyenPaymentMethod = (
  p: PaymentMethod
): p is PaymentMethod & { payment_source_type: 'adyen_payments' } =>
  p.payment_source_type === 'adyen_payments'

/**
 * Retrieves the ID of the Adyen payment method from an order.
 *
 * @param {Order} order - The order to extract the Adyen payment method from.
 * @returns {string | undefined} The ID of the Adyen payment method, or undefined if not found.
 * @example
 * const adyenPaymentMethodId = getAdyenPaymentMethodId(myOrder);
 * if (adyenPaymentMethodId) {
 *   // Use the Adyen payment method ID
 * }
 */
export const getAdyenPaymentMethodId = (order: Order) => {
  const adyenPaymentMethod =
    order?.available_payment_methods?.find(isAdyenPaymentMethod)

  return adyenPaymentMethod?.id
}
